import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import { DeviceFamilyGet, DeviceFamilyPut } from "../../../../models/DeviceFamily"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  isRequestable: z.boolean({ error: "Is Requestable is required" }),
})

const returnURL = `/devices#families`

export function FamilyDetails(props: {
  readonly deviceFamily: DeviceFamilyGet
  readonly setDeviceFamily: (updatedDeviceFamily: DeviceFamilyGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { deviceFamily, setDeviceFamily } = props

  const formMethods = useForm<DeviceFamilyPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: deviceFamily.name,
      description: deviceFamily.description,
      isRequestable: deviceFamily.isRequestable,
    })
  }, [deviceFamily, reset])

  const onSubmit = useCallback(
    (values: DeviceFamilyPut) =>
      digitalManagerApi
        .put<DeviceFamilyGet>(`/deviceFamilies/${deviceFamily.id}`, {
          name: values.name,
          description: values.description,
          isRequestable: values.isRequestable,
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Device Family updated")
            setDeviceFamily(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update device family")
          throw new Error("Cannot update device family")
        }),
    [deviceFamily.id, setDeviceFamily]
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<DeviceFamilyPut, "name" | "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceFamilyPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceFamilyPut, "description"> }) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextFieldChange]
  )

  const handleCheckboxToggle = useCallback(
    (field: ControllerRenderProps<DeviceFamilyPut, "isRequestable">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderIsRequestableCheckbox = useCallback(
    ({ field }: { field: ControllerRenderProps<DeviceFamilyPut, "isRequestable"> }) => (
      <MUICheckBox
        {...field}
        id="isRequestable"
        checked={field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<DeviceFamilyPut> = {
    formMethods,
    canWrite: hasUserAccessToFeature(
      user.authorizations,
      DimaFeatureShortNameEnum.DM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{deviceFamily.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
      {
        id: 1,
        label: "Description",
        displayItem: <div className="title">{deviceFamily.description}</div>,
        editItem: (
          <Controller
            name="description"
            control={control}
            render={renderDescriptionTextField}
          />
        ),
      },
      {
        id: 2,
        label: "Is Requestable",
        displayItem: (
          <div className="title">{deviceFamily.isRequestable?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isRequestable"
            control={control}
            render={renderIsRequestableCheckbox}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
